import { useCallback } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';

import { IRestaurant } from '@rbi-ctg/store';
import { useDeeplinksFlowEnabled } from 'experiments/deeplinks-flow-feature/use-deeplinks-flow-experiment';
import { ServiceMode } from 'generated/graphql-gateway';
import { getStaticPathname } from 'navigation/location/location';
import { appendObjectToQueryString } from 'navigation/utils';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useOrderContext } from 'state/order';
import { ISelectOrderStore } from 'state/order/types';
import { useServiceModeContext } from 'state/service-mode';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { routes } from 'utils/routing';

import { useDefaultServiceMode } from './use-default-service-mode';

/** Item/combo/picker PDP under `(modals)/menu/[menuObjectType]/[id]`. */
const MENU_PRODUCT_REDIRECT_PATTERN = /^\/menu\/(item|combo|picker)\/([^/?#]+)/i;

export function parseMenuProductRedirect(
  redirectUrl: string
): { menuObjectType: string; id: string } | null {
  const path = redirectUrl.split('?')[0] ?? '';
  const match = path.match(MENU_PRODUCT_REDIRECT_PATTERN);
  if (!match?.[1] || !match[2]) {
    return null;
  }
  return {
    menuObjectType: match[1].toLowerCase(),
    id: match[2],
  };
}

export function useSelectStore() {
  const {
    selectOrderStore,
    reorder: { pendingReorder, setShouldHandleReorder },
  } = useOrderContext();
  const { redirectUrl, targetItemIds, unavailableItemId, offerEngineId } = useLocalSearchParams<{
    redirectUrl?: string;
    targetItemIds?: string | string[];
    unavailableItemId?: string | string[];
    offerEngineId?: string | string[];
  }>();

  const { selectServiceMode } = useServiceModeContext();
  const router = useRouter();
  const isDeeplinksFlowEnabled = useDeeplinksFlowEnabled();

  const getDefaultServiceMode = useDefaultServiceMode();

  const dismissStoreLocator = useCallback(() => {
    const isStoreInfoModal = getStaticPathname().startsWith(routes.store);
    if (isStoreInfoModal) {
      router.dismiss();
      router.back();
    } else if (redirectUrl) {
      // Store-locator search params may include menu pin ids.
      // The dismiss target is only `redirectUrl` (path), so re-attach them here.
      const normalizedTargetItemIds = Array.isArray(targetItemIds)
        ? targetItemIds.join(',')
        : targetItemIds?.trim();
      const normalizedUnavailableItemId = Array.isArray(unavailableItemId)
        ? unavailableItemId[0]?.trim()
        : unavailableItemId?.trim();
      const normalizedOfferEngineId = Array.isArray(offerEngineId)
        ? offerEngineId[0]?.trim()
        : offerEngineId?.trim();

      const isOffersRedirect =
        redirectUrl === routes.offers || redirectUrl.startsWith(`${routes.offers}?`);
      if (isDeeplinksFlowEnabled && isOffersRedirect && normalizedOfferEngineId) {
        router.dismissTo({
          pathname: '/offers/[loyaltyEngineId]',
          params: { loyaltyEngineId: normalizedOfferEngineId },
        });
        return;
      }

      // After Tap-here store pick, open the menu PDP so availability is re-checked.
      const menuProductRedirect = parseMenuProductRedirect(redirectUrl);
      if (isDeeplinksFlowEnabled && menuProductRedirect) {
        router.dismissTo({
          pathname: '/menu/[menuObjectType]/[id]',
          params: menuProductRedirect,
        });
        return;
      }

      const query: Record<string, string> = {};
      if (normalizedTargetItemIds) {
        query.targetItemIds = normalizedTargetItemIds;
      }
      // Treatment only: re-attach unavailableItemId for section chrome.
      if (isDeeplinksFlowEnabled && normalizedUnavailableItemId) {
        query.unavailableItemId = normalizedUnavailableItemId;
      }
      if (isDeeplinksFlowEnabled && normalizedOfferEngineId) {
        query.offerEngineId = normalizedOfferEngineId;
      }
      const dismissHref = Object.keys(query).length
        ? appendObjectToQueryString(redirectUrl, query)
        : redirectUrl;

      router.dismissTo(dismissHref);
    } else {
      // Avoid dismissing arbitrary modals when selection is triggered outside the
      // store locator flow (e.g., deep links/autoselect). Let the caller control navigation.
      return;
    }
  }, [
    isDeeplinksFlowEnabled,
    offerEngineId,
    redirectUrl,
    router,
    targetItemIds,
    unavailableItemId,
  ]);

  const selectStoreCallback = useCallback(
    async ({ newStore, serviceMode }: { newStore: IRestaurant; serviceMode: ServiceMode }) => {
      dismissStoreLocator();
      // TODO: We probably want to use dynamic event name here
      logEvent(CustomEventNames.ORDER_HERE_STORE_CONFIRMATION_FLOW, EventTypes.Navigation, {
        'Restaurant Id': newStore._id,
        'Restaurant Name': newStore.name,
      });
      // set service mode to ensure we aren't taken back to static menu
      // need to do this after previous checks so that we don't set service mode
      // when enableOrdering is false
      selectServiceMode(serviceMode);

      // if user has a pending reorder return and let the useEffect handle it below
      if (pendingReorder) {
        setShouldHandleReorder(true);
        return;
      }

      // Suppress the closer-store drawer after an explicit store choice (locator / store-info).
      syncStorage.setNumber(SyncStorageKey.AUTO_SELECT_STORE_TIMESTAMP, Date.now());
    },
    [selectServiceMode, pendingReorder, dismissStoreLocator, setShouldHandleReorder]
  );

  return useCallback(
    async ({
      newStore,
      requestedServiceMode,
      skipChangeStoreMessage,
      callback,
    }: ISelectOrderStore) => {
      const defaultServiceMode = getDefaultServiceMode(newStore);
      const finalServiceMode = requestedServiceMode ?? defaultServiceMode;

      await selectOrderStore({
        newStore,
        requestedServiceMode: finalServiceMode,
        skipChangeStoreMessage,
        callback: () => {
          selectStoreCallback({
            newStore,
            serviceMode: finalServiceMode,
          });
          callback?.();
        },
      });
    },
    [getDefaultServiceMode, selectOrderStore, selectStoreCallback]
  );
}
